import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import { ProfitLossData, ProfitLossPrimaryGroup, ProfitLossSubGroup, ProfitLossLedger } from '../../utils/report.types';

interface ProfitLossStatementProps {
  data: ProfitLossData | null;
  loading?: boolean;
  error?: string;
}

const ProfitLossStatement: React.FC<ProfitLossStatementProps> = ({ data, loading, error }) => {
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const renderLedgers = (ledgers: ProfitLossLedger[]) => {
    if (!ledgers || ledgers.length === 0) return null;
    return ledgers.map((ledger) => (
      <TableRow key={ledger._id}>
        <TableCell sx={{ pl: 6, fontSize: '9pt', color: '#424242', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px' }}>
          {ledger.name}
        </TableCell>
        <TableCell align="right" sx={{ fontSize: '9pt', color: '#424242', border: '0.75px solid #d9d4d4', padding: '5px', paddingRight: '15px' }}>
          {formatAmount(ledger.balance)}
        </TableCell>
      </TableRow>
    ));
  };

  const renderSubGroups = (subGroups: ProfitLossSubGroup[]) => {
    if (!subGroups || subGroups.length === 0) return null;
    return subGroups.map((subGroup) => (
      <React.Fragment key={subGroup._id}>
        <TableRow>
          <TableCell sx={{ pl: 4, fontSize: '9pt', fontWeight: 500, color: '#616161', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px' }}>
            {subGroup.name}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '9pt', fontWeight: 500, color: '#616161', border: '0.75px solid #d9d4d4', padding: '5px', paddingRight: '15px' }}>
            {formatAmount(subGroup.total)}
          </TableCell>
        </TableRow>
        {renderLedgers(subGroup.ledgers)}
      </React.Fragment>
    ));
  };

  const renderPrimaryGroups = (groups: ProfitLossPrimaryGroup[]) => {
    if (!groups || groups.length === 0) return null;
    return groups.map((group) => (
      <React.Fragment key={group._id}>
        <TableRow>
          <TableCell sx={{ pl: 2, fontSize: '9pt', fontWeight: 600, color: '#212121', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px' }}>
            {group.name}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '9pt', fontWeight: 600, color: '#212121', border: '0.75px solid #d9d4d4', padding: '5px', paddingRight: '15px' }}>
            {formatAmount(group.total)}
          </TableCell>
        </TableRow>
        {group.subGroups && renderSubGroups(group.subGroups)}
        {group.ledgers && renderLedgers(group.ledgers)}
      </React.Fragment>
    ));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No data available. Please select a date range and generate the report.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2}>
        {/* LEFT COLUMN - REVENUE & COGS */}
        <Grid item xs={12} md={6}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {/* REVENUE HEADER */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ backgroundColor: '#eaecf0', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px', fontWeight: 700, fontSize: '9pt' }}>
                    REVENUE
                  </TableCell>
                </TableRow>

                {/* Sales Income */}
                {data.revenue.salesIncome.breakdown && renderPrimaryGroups(data.revenue.salesIncome.breakdown)}

                {/* Spacer */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ height: 12, border: 'none' }} />
                </TableRow>

                {/* Other Income Section */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ backgroundColor: '#eaecf0', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px', fontWeight: 700, fontSize: '9pt' }}>
                    OTHER INCOME
                  </TableCell>
                </TableRow>

                {data.revenue.otherIncome.breakdown && data.revenue.otherIncome.breakdown.length > 0 ? (
                  renderPrimaryGroups(data.revenue.otherIncome.breakdown)
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '30px', fontSize: '9pt', fontStyle: 'italic', color: '#9ca3af' }}>
                      (No records)
                    </TableCell>
                  </TableRow>
                )}

                {/* Other Income Total */}
                {data.revenue.otherIncome.total > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '9pt', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px', backgroundColor: '#fafafa' }}>
                      Total Other Income
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '9pt', border: '0.75px solid #d9d4d4', padding: '5px', paddingRight: '15px', backgroundColor: '#fafafa' }}>
                      {formatAmount(data.revenue.otherIncome.total)}
                    </TableCell>
                  </TableRow>
                )}

                {/* Spacer */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ height: 12, border: 'none' }} />
                </TableRow>

                {/* Total Revenue */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '10pt', border: '0.75px solid #d9d4d4', borderTop: '2px solid #64748b', padding: '5px', paddingLeft: '15px', backgroundColor: '#f1f5f9' }}>
                    TOTAL REVENUE
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '10pt', border: '0.75px solid #d9d4d4', borderTop: '2px solid #64748b', padding: '5px', paddingRight: '15px', backgroundColor: '#f1f5f9' }}>
                    {formatAmount(data.revenue.totalRevenue)}
                  </TableCell>
                </TableRow>

                {/* Spacer */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ height: 16, border: 'none' }} />
                </TableRow>

                {/* COGS HEADER */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ backgroundColor: '#eaecf0', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px', fontWeight: 700, fontSize: '9pt' }}>
                    COST OF GOODS SOLD (COGS)
                  </TableCell>
                </TableRow>

                {/* COGS Items */}
                {data.cogs.breakdown && renderPrimaryGroups(data.cogs.breakdown)}

                {/* Total COGS */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '10pt', border: '0.75px solid #d9d4d4', borderTop: '2px solid #64748b', padding: '5px', paddingLeft: '15px', backgroundColor: '#f1f5f9' }}>
                    TOTAL COGS
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '10pt', border: '0.75px solid #d9d4d4', borderTop: '2px solid #64748b', padding: '5px', paddingRight: '15px', backgroundColor: '#f1f5f9' }}>
                    {formatAmount(data.cogs.total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* RIGHT COLUMN - OPERATING EXPENSES */}
        <Grid item xs={12} md={6}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {/* OPERATING EXPENSES HEADER */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ backgroundColor: '#eaecf0', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px', fontWeight: 700, fontSize: '9pt' }}>
                    OPERATING EXPENSES
                  </TableCell>
                </TableRow>

                {/* Operating Expenses Items */}
                {data.operatingExpenses.breakdown && data.operatingExpenses.breakdown.length > 0 ? (
                  renderPrimaryGroups(data.operatingExpenses.breakdown)
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '30px', fontSize: '9pt', fontStyle: 'italic', color: '#9ca3af' }}>
                      (No records)
                    </TableCell>
                  </TableRow>
                )}

                {/* Total Operating Expenses */}
                {data.operatingExpenses.total > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '10pt', border: '0.75px solid #d9d4d4', borderTop: '2px solid #64748b', padding: '5px', paddingLeft: '15px', backgroundColor: '#f1f5f9' }}>
                      TOTAL OPERATING EXPENSES
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '10pt', border: '0.75px solid #d9d4d4', borderTop: '2px solid #64748b', padding: '5px', paddingRight: '15px', backgroundColor: '#f1f5f9' }}>
                      {formatAmount(data.operatingExpenses.total)}
                    </TableCell>
                  </TableRow>
                )}

                {/* Spacer */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ height: 12, border: 'none' }} />
                </TableRow>

                {/* Other Expenses Section */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ backgroundColor: '#eaecf0', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px', fontWeight: 700, fontSize: '9pt' }}>
                    OTHER EXPENSES
                  </TableCell>
                </TableRow>

                {data.otherExpenses && data.otherExpenses.breakdown && data.otherExpenses.breakdown.length > 0 ? (
                  renderPrimaryGroups(data.otherExpenses.breakdown)
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '30px', fontSize: '9pt', fontStyle: 'italic', color: '#9ca3af' }}>
                      (No records)
                    </TableCell>
                  </TableRow>
                )}

                {/* Total Other Expenses */}
                {data.otherExpenses && data.otherExpenses.total > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '9pt', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px', backgroundColor: '#fafafa' }}>
                      Total Other Expenses
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '9pt', border: '0.75px solid #d9d4d4', padding: '5px', paddingRight: '15px', backgroundColor: '#fafafa' }}>
                      {formatAmount(data.otherExpenses.total)}
                    </TableCell>
                  </TableRow>
                )}

                {/* Spacer */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ height: 12, border: 'none' }} />
                </TableRow>

                {/* Round Off Section */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ backgroundColor: '#eaecf0', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px', fontWeight: 700, fontSize: '9pt' }}>
                    ROUND OFF
                  </TableCell>
                </TableRow>

                {/* Round Off Income - Single Aggregated Entry */}
                {data.roundOff.income && data.roundOff.income.total > 0 && (
                  <TableRow>
                    <TableCell sx={{ pl: 2, fontSize: '9pt', color: '#424242', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '30px' }}>
                      {data.roundOff.income.ledger?.name || 'Round Off (Income)'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '9pt', color: '#16a34a', border: '0.75px solid #d9d4d4', padding: '5px', paddingRight: '15px' }}>
                      {formatAmount(data.roundOff.income.total)}
                    </TableCell>
                  </TableRow>
                )}

                {/* Round Off Expense - Single Aggregated Entry */}
                {data.roundOff.expense && data.roundOff.expense.total > 0 && (
                  <TableRow>
                    <TableCell sx={{ pl: 2, fontSize: '9pt', color: '#424242', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '30px' }}>
                      {data.roundOff.expense.ledger?.name || 'Round Off (Expense)'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '9pt', color: '#dc2626', border: '0.75px solid #d9d4d4', padding: '5px', paddingRight: '15px' }}>
                      {formatAmount(data.roundOff.expense.total)}
                    </TableCell>
                  </TableRow>
                )}

                {/* No Round Off */}
                {(!data.roundOff.income || data.roundOff.income.total === 0) && (!data.roundOff.expense || data.roundOff.expense.total === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '30px', fontSize: '9pt', fontStyle: 'italic', color: '#9ca3af' }}>
                      (No records)
                    </TableCell>
                  </TableRow>
                )}

                {/* Net Round Off Total */}
                {((data.roundOff.income && data.roundOff.income.total > 0) || (data.roundOff.expense && data.roundOff.expense.total > 0)) && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '9pt', border: '0.75px solid #d9d4d4', padding: '5px', paddingLeft: '15px', backgroundColor: '#fafafa' }}>
                      Net Round Off
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '9pt', border: '0.75px solid #d9d4d4', padding: '5px', paddingRight: '15px', backgroundColor: '#fafafa', color: data.roundOff.netRoundOff > 0 ? '#dc2626' : '#16a34a' }}>
                      {formatAmount(data.roundOff.netRoundOff)}
                    </TableCell>
                  </TableRow>
                )}

                {/* Spacer */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ height: 16, border: 'none' }} />
                </TableRow>

                {/* Profit Before Income Tax */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '10pt', border: '0.75px solid #d9d4d4', borderTop: '2px solid #64748b', padding: '5px', paddingLeft: '15px', backgroundColor: '#f1f5f9' }}>
                    PROFIT BEFORE INCOME TAX
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '10pt', border: '0.75px solid #d9d4d4', borderTop: '2px solid #64748b', padding: '5px', paddingRight: '15px', backgroundColor: '#f1f5f9' }}>
                    {formatAmount(data.netProfitBeforeTax)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* BOTTOM SUMMARY SECTION */}
      <Box sx={{ mt: 3, mb: 3, p: 3, backgroundColor: '#fef3c7', border: '2px solid #f59e0b', borderRadius: 1 }}>
        <Grid container spacing={4}>
          {/* Gross Profit */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#92400e' }}>
              GROSS PROFIT
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: data.grossProfit >= 0 ? '#2e7d32' : '#d32f2f' }}>
              {formatAmount(data.grossProfit)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#92400e', mt: 0.5 }}>
              Margin: {data.summary.grossProfitMargin}%
            </Typography>
          </Grid>

          {/* Net Profit */}
          <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#92400e' }}>
              NET PROFIT
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: data.netProfitAfterTax >= 0 ? '#2e7d32' : '#d32f2f' }}>
              {formatAmount(data.netProfitAfterTax)}
            </Typography>
            <Typography variant="body2" sx={{ color: '#92400e', mt: 0.5 }}>
              Margin: {data.summary.netProfitMargin}%
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProfitLossStatement;
